import { theme } from "antd";

const { defaultAlgorithm, darkAlgorithm } = theme;

// Single Dark Theme Configuration — SPA Inspired
// This is the single source of truth for all colors and tokens.
export const darkTheme = {
	algorithm: darkAlgorithm,
	token: {
		// Brand colors - Spa palette (calm, cool, grounded)
		colorPrimary: "#6B9E8D",
		colorSuccess: "#7EBBA3",
		colorWarning: "#C9A876",
		colorError: "#D4847A",
		colorInfo: "#6B9E8D",

		// Background colors - Deep, calming neutrals
		colorBgBase: "#1A1F1D",
		colorBgContainer: "#212825",
		colorBgElevated: "#2B3330",
		colorBgLayout: "#1A1F1D",
		colorBgSpotlight: "rgba(18, 18, 16, 0.85)",

		// Title Header BG color
		colorTitleHeaderBg: "#1A1F1D",

		// Border colors - Subtle, natural tones
		borderRadius: 8,
		colorBorder: "#3A4542",
		colorBorderSecondary: "#2F3834",

		// Text colors - Light earthy tones
		colorText: "#E8E5E0",
		colorTextSecondary: "#B8B5B0",
		colorTextTertiary: "#8F8B86",
		colorTextQuaternary: "#626259",
		colorTextPlaceholder: "#6B9E8D",

		// Font
		fontSize: 14,
		fontFamily:
			'-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif',

		// Control components
		controlHeight: 32,
		controlHeightLG: 40,
		controlHeightSM: 24,

		// Motion
		motion: true,
		motionBase: 0,
		motionUnit: 0.1,

		// Spacing - Consistent spacing across components
		marginXXXS: 4,
		marginXXS: 8,
		marginXS: 12,
		marginSM: 16,
		margin: 16,
		marginMD: 16,
		marginLG: 24,
		marginXL: 32,
		marginXXL: 48,

		paddingXXXS: 4,
		paddingXXS: 8,
		paddingXS: 12,
		paddingSM: 16,
		padding: 16,
		paddingMD: 16,
		paddingLG: 24,
		paddingXL: 32,
		paddingXXL: 48,

		// Box shadow - Soft, gentle shadows
		boxShadow: "0 2px 8px rgba(0, 0, 0, 0.20), 0 1px 3px rgba(0, 0, 0, 0.15)",
		boxShadowSecondary:
			"0 4px 12px rgba(0, 0, 0, 0.25), 0 2px 4px rgba(0, 0, 0, 0.20)",

		// Fill colors - Subtle, natural fills
		colorFill: "rgba(126, 187, 163, 0.12)",
		colorFillSecondary: "rgba(126, 187, 163, 0.06)",
		colorFillTertiary: "rgba(126, 187, 163, 0.03)",
		colorFillQuaternary: "rgba(126, 187, 163, 0.01)",

		// Primary colors
		colorPrimaryBgHover: "rgba(107, 158, 141, 0.12)",

		// User menu specific colors
		colorBgUserHeader: "rgba(126, 187, 163, 0.15)",
		colorBgUserInfo: "#1A1F1D",
		colorPrimaryLight: "rgba(107, 158, 141, 0.20)",

		// Logo colors - Dark theme
		colorLogoText: "#6B9E8D",
		colorLogoLight: "#F8F7F5",
		colorLogoDark: "#282F2D",

		// Brand color RGBA versions
		colorPrimaryRgba: "rgba(107, 158, 141, 0.4)",
		colorSuccessRgba: "rgba(126, 187, 163, 0.4)",
		colorWarningRgba: "rgba(201, 168, 118, 0.4)",
		colorErrorRgba: "rgba(212, 132, 122, 0.4)",

		// Logo color RGBA versions
		colorLogoTextRgba: "rgba(107, 158, 141, 0.4)",
		colorLogoLightRgba: "rgba(248, 247, 245, 0.4)",
		colorLogoDarkRgba: "rgba(40, 47, 45, 0.4)",

		// Section Separator Colors - Spa Palette
		colorPink: "#C9A5A0",
		colorPurple: "#8B7B9D",
		colorBlue: "#6B8FBE",
		colorGreen: "#6B9E8D",
		colorOrange: "#C9A876",
		colorRed: "#D4847A",

		// RGBA Colors for shadows and transparency effects
		colorPinkRgba: "rgba(201, 165, 160, 0.4)",
		colorPurpleRgba: "rgba(139, 123, 157, 0.4)",
		colorBlueRgba: "rgba(107, 143, 190, 0.4)",
		colorGreenRgba: "rgba(107, 158, 141, 0.4)",
		colorOrangeRgba: "rgba(201, 168, 118, 0.4)",
		colorRedRgba: "rgba(212, 132, 122, 0.4)",

		// Additional RGBA variations
		colorOrangeRgbaLight: "rgba(201, 168, 118, 0.2)",
		colorOrangeRgbaMedium: "rgba(201, 168, 118, 0.3)",
		colorOrangeRgbaStrong: "rgba(201, 168, 118, 0.6)",
		colorBlueRgbaLight: "rgba(107, 143, 190, 0.15)",
		colorBlueRgbaMedium: "rgba(107, 143, 190, 0.35)",
		colorBlueRgbaVideo: "rgba(107, 158, 141, 0.25)",

		// Overlay colors
		darkOverlay: "rgba(26, 31, 29, 0.30)",
		whiteTransparent: "rgba(255, 255, 255, 0)",
		whiteSolid: "rgba(255, 255, 255, 1)",

		// White button colors
		whiteButtonBg: "rgba(255, 255, 255, 0.15)",
		whiteButtonBgHover: "rgba(255, 255, 255, 0.25)",
		whiteButtonBorder: "rgba(255, 255, 255, 0.20)",
		whiteButtonBorderHover: "rgba(255, 255, 255, 0.35)",
		whiteScrollBg: "rgba(255, 255, 255, 0.12)",

		// Spa Pastel Green Colors - Grounded & Calm
		pastelGreen: "#7EBBA3",
		pastelGreenRgba: "rgba(126, 187, 163, 0.25)",
		pastelGreenHover: "rgba(126, 187, 163, 0.40)",

		// Audio Player Play/Pause Button colors - Dark theme
		playButtonPlayingBg: "#6B9E8D",
		playButtonPlayingText: "#FFFFFF",
		playButtonPausedBg: "#2F3834",
		playButtonPausedText: "#E8E5E0",

		// Avatar colors - Dark theme
		avatarBg: "#6B9E8D",
		avatarColor: "#FFFFFF",
	},
	components: {
		Card: {
			colorBgContainer: "rgba(43, 51, 48, 0.95)",
			boxShadowTertiary: "0 2px 6px rgba(107, 158, 141, 0.12)",
			colorBorderSecondary: "#3A4542",
		},
		Button: {
			colorPrimaryHover: "#7EBBA3",
			colorPrimaryActive: "#5A8A76",
		},
		Input: {
			colorBgContainer: "#212825",
			colorBorder: "#3A4542",
		},
		Layout: {
			headerBg: "#212825",
			bodyBg: "#1A1F1D",
			triggerBg: "#2B3330",
			colorBgSidebar: "#1F2623",
		},
		Menu: {
			colorBgContainer: "transparent",
			itemBg: "transparent",
			itemHoverBg: "rgba(126, 187, 163, 0.08)",
			itemSelectedBg: "rgba(107, 158, 141, 0.18)",
			itemColor: "#8F8B86",
			itemHoverColor: "#B8B5B0",
			itemSelectedColor: "#7EBBA3",
		},
		Badge: {
			colorBgContainer: "#D4847A",
			colorText: "#ffffff",
		},
		Table: {
			colorBgContainer: "transparent",
			colorFillAlter: "rgba(126, 187, 163, 0.04)",
			colorBorderSecondary: "rgba(126, 187, 163, 0.10)",
			headerBg: "rgba(126, 187, 163, 0.08)",
			rowHoverBg: "rgba(126, 187, 163, 0.12)",
		},
		Dropdown: {
			colorBgElevated: "#2B3330",
			colorBorder: "#3A4542",
			boxShadowSecondary:
				"0 4px 12px rgba(0, 0, 0, 0.25), 0 2px 4px rgba(0, 0, 0, 0.20)",
		},
		Typography: {
			colorText: "#E8E5E0",
			colorTextSecondary: "#B8B5B0",
		},
		Slider: {
			railBg: "#2F3834",
			trackBg: "#6B9E8D",
			handleColor: "#6B9E8D",
			handleBorderColor: "#6B9E8D",
		},
		Drawer: {
			colorBgElevated: "#2B3330",
			colorBorder: "#3A4542",
			boxShadow:
				"0 4px 12px rgba(0, 0, 0, 0.25), 0 2px 4px rgba(0, 0, 0, 0.20)",
			padding: 24,
			styles: {
				header: {
					backgroundColor: "#2B3330",
					borderBottom: "1px solid #2F3834",
				},
				body: {
					backgroundColor: "#212825",
				},
			},
		},
		Row: {
			gutter: 16,
		},
		Col: {
			gutter: 16,
		},
	},
};

// Light theme for admin UI
export const lightTheme = {
	algorithm: defaultAlgorithm,
	token: {
		// Brand colors - Spa palette (calm, cool, grounded)
		colorPrimary: "#6B9E8D",
		colorSuccess: "#5A9B80",
		colorWarning: "#B8923E",
		colorError: "#C44B3F",
		colorInfo: "#6B9E8D",

		// Background colors - Clean, light neutrals
		colorBgBase: "#F8F7F5",
		colorBgContainer: "#FFFFFF",
		colorBgElevated: "#FFFFFF",
		colorBgLayout: "#F5F6F8",
		// Tooltip background — must stay dark so the default light tooltip text
		// is legible (white-on-white was the bug). Brand obsidian tint.
		colorBgSpotlight: "rgba(26, 31, 29, 0.92)",

		// Title Header BG color
		colorTitleHeaderBg: "#FFFFFF",

		// Border colors - Subtle, natural tones
		borderRadius: 8,
		colorBorder: "#E5E7EB",
		colorBorderSecondary: "#F3F4F6",

		// Text colors - Dark earthy tones
		colorText: "#1F2937",
		colorTextSecondary: "#4B5563",
		colorTextTertiary: "#6B7280",
		colorTextQuaternary: "#9CA3AF",
		colorTextPlaceholder: "#9CA3AF",

		// Font
		fontSize: 14,
		fontFamily:
			'-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif',

		// Control components
		controlHeight: 32,
		controlHeightLG: 40,
		controlHeightSM: 24,

		// Motion
		motion: true,
		motionBase: 0,
		motionUnit: 0.1,

		// Spacing
		marginXXXS: 4,
		marginXXS: 8,
		marginXS: 12,
		marginSM: 16,
		margin: 16,
		marginMD: 16,
		marginLG: 24,
		marginXL: 32,
		marginXXL: 48,

		paddingXXXS: 4,
		paddingXXS: 8,
		paddingXS: 12,
		paddingSM: 16,
		padding: 16,
		paddingMD: 16,
		paddingLG: 24,
		paddingXL: 32,
		paddingXXL: 48,

		// Box shadow - Soft shadows for light theme
		boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.06)",
		boxShadowSecondary:
			"0 4px 12px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)",

		// Fill colors - Subtle, natural fills
		colorFill: "rgba(107, 158, 141, 0.08)",
		colorFillSecondary: "rgba(107, 158, 141, 0.04)",
		colorFillTertiary: "rgba(107, 158, 141, 0.03)",
		colorFillQuaternary: "rgba(107, 158, 141, 0.02)",

		// Primary colors
		colorPrimaryBgHover: "rgba(107, 158, 141, 0.08)",

		// User menu specific colors
		colorBgUserHeader: "rgba(107, 158, 141, 0.08)",
		colorBgUserInfo: "#FFFFFF",
		colorPrimaryLight: "rgba(107, 158, 141, 0.12)",

		// Logo colors - Light theme
		colorLogoText: "#6B9E8D",
		colorLogoLight: "#F8F7F5",
		colorLogoDark: "#282F2D",

		// Brand color RGBA versions
		colorPrimaryRgba: "rgba(107, 158, 141, 0.4)",
		colorSuccessRgba: "rgba(90, 155, 128, 0.4)",
		colorWarningRgba: "rgba(184, 146, 62, 0.4)",
		colorErrorRgba: "rgba(196, 75, 63, 0.4)",

		// Logo color RGBA versions
		colorLogoTextRgba: "rgba(107, 158, 141, 0.4)",
		colorLogoLightRgba: "rgba(248, 247, 245, 0.4)",
		colorLogoDarkRgba: "rgba(40, 47, 45, 0.4)",

		// Section Separator Colors - Spa Palette
		colorPink: "#C9A5A0",
		colorPurple: "#8B7B9D",
		colorBlue: "#6B8FBE",
		colorGreen: "#6B9E8D",
		colorOrange: "#C9A876",
		colorRed: "#D4847A",

		// RGBA Colors for shadows and transparency effects
		colorPinkRgba: "rgba(201, 165, 160, 0.4)",
		colorPurpleRgba: "rgba(139, 123, 157, 0.4)",
		colorBlueRgba: "rgba(107, 143, 190, 0.4)",
		colorGreenRgba: "rgba(107, 158, 141, 0.4)",
		colorOrangeRgba: "rgba(201, 168, 118, 0.4)",
		colorRedRgba: "rgba(212, 132, 122, 0.4)",

		// Additional RGBA variations
		colorOrangeRgbaLight: "rgba(201, 168, 118, 0.2)",
		colorOrangeRgbaMedium: "rgba(201, 168, 118, 0.3)",
		colorOrangeRgbaStrong: "rgba(201, 168, 118, 0.6)",
		colorBlueRgbaLight: "rgba(107, 143, 190, 0.15)",
		colorBlueRgbaMedium: "rgba(107, 143, 190, 0.35)",
		colorBlueRgbaVideo: "rgba(107, 158, 141, 0.25)",

		// Overlay colors
		darkOverlay: "rgba(0, 0, 0, 0.04)",
		whiteTransparent: "rgba(255, 255, 255, 0)",
		whiteSolid: "rgba(255, 255, 255, 1)",

		// White button colors
		whiteButtonBg: "rgba(255, 255, 255, 0.85)",
		whiteButtonBgHover: "rgba(255, 255, 255, 0.95)",
		whiteButtonBorder: "rgba(229, 231, 235, 0.8)",
		whiteButtonBorderHover: "rgba(209, 213, 219, 0.9)",
		whiteScrollBg: "rgba(0, 0, 0, 0.04)",

		// Spa Pastel Green Colors
		pastelGreen: "#7EBBA3",
		pastelGreenRgba: "rgba(126, 187, 163, 0.25)",
		pastelGreenHover: "rgba(126, 187, 163, 0.40)",

		// Audio Player Play/Pause Button colors - Light theme
		playButtonPlayingBg: "#6B9E8D",
		playButtonPlayingText: "#FFFFFF",
		playButtonPausedBg: "#F3F4F6",
		playButtonPausedText: "#1F2937",

		// Avatar colors - Light theme
		avatarBg: "#6B9E8D",
		avatarColor: "#FFFFFF",
	},
	components: {
		Card: {
			colorBgContainer: "#FFFFFF",
			boxShadowTertiary: "0 1px 3px rgba(0, 0, 0, 0.08)",
			colorBorderSecondary: "#E5E7EB",
		},
		Button: {
			colorPrimaryHover: "#7EBBA3",
			colorPrimaryActive: "#5A8A76",
		},
		Input: {
			colorBgContainer: "#FFFFFF",
			colorBorder: "#D1D5DB",
		},
		Layout: {
			headerBg: "#FFFFFF",
			bodyBg: "#F5F6F8",
			triggerBg: "#F9FAFB",
			colorBgSidebar: "#FFFFFF",
		},
		Menu: {
			colorBgContainer: "#FFFFFF",
			itemBg: "transparent",
			itemHoverBg: "rgba(107, 158, 141, 0.06)",
			itemSelectedBg: "rgba(107, 158, 141, 0.12)",
			itemColor: "#6B7280",
			itemHoverColor: "#1F2937",
			itemSelectedColor: "#6B9E8D",
		},
		Badge: {
			colorBgContainer: "#C44B3F",
			colorText: "#ffffff",
		},
		Table: {
			colorBgContainer: "#FFFFFF",
			colorFillAlter: "rgba(107, 158, 141, 0.03)",
			colorBorderSecondary: "#E5E7EB",
			headerBg: "rgba(107, 158, 141, 0.06)",
			rowHoverBg: "rgba(107, 158, 141, 0.08)",
		},
		Dropdown: {
			colorBgElevated: "#FFFFFF",
			colorBorder: "#E5E7EB",
			boxShadowSecondary:
				"0 4px 12px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)",
		},
		Typography: {
			colorText: "#1F2937",
			colorTextSecondary: "#4B5563",
		},
		Slider: {
			railBg: "#E5E7EB",
			trackBg: "#6B9E8D",
			handleColor: "#6B9E8D",
			handleBorderColor: "#6B9E8D",
		},
		Drawer: {
			colorBgElevated: "#FFFFFF",
			colorBorder: "#E5E7EB",
			boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)",
			padding: 24,
		},
		Row: {
			gutter: 16,
		},
		Col: {
			gutter: 16,
		},
	},
};
